import { API_ENDPOINTS } from '@/config/apiEndpoints';
import { config } from '@/config/env';
import { authApiClient } from '@/shared/utils/api/apiClient';
import { toApiError } from '@/shared/utils/api/apiErrorHandler';
import { ApiError } from '@/shared/types/api.types';
import { toUserId } from '@/shared/types/common.types';
import { tokenService, toAuthSession } from './tokenService';
import { authenticationResponseSchema, USER_ROLES } from '../types/auth.types';
import type {
  AuthenticationResponse,
  AuthenticatedUser,
  LoginCredentials,
  UserRole,
} from '../types/auth.types';

function normalizeRole(value: string): readonly UserRole[] {
  return (USER_ROLES as readonly string[]).includes(value) ? [value as UserRole] : [];
}

function mapUser(response: NonNullable<AuthenticationResponse['user']>): AuthenticatedUser {
  return {
    id: toUserId(response.id),
    username: response.username,
    email: response.email,
    emailVerified: response.emailVerified ?? false,
    givenName: response.givenName,
    familyName: response.familyName,
    ...(response.middleName === undefined ? {} : { middleName: response.middleName }),
    ...(response.phoneNumber === undefined ? {} : { phoneNumber: response.phoneNumber }),
    phoneNumberVerified: response.phoneNumberVerified ?? false,
    ...(response.picture === undefined || response.picture.length === 0 ? {} : { picture: response.picture }),
    ...(response.organization === undefined || response.organization.length === 0
      ? {}
      : { organization: response.organization }),
    ...(response.department === undefined || response.department.length === 0
      ? {}
      : { department: response.department }),
    ...(response.employeeId === undefined || response.employeeId.length === 0
      ? {}
      : { employeeId: response.employeeId }),
    roles: normalizeRole(response.role),
    ...(response.locale === undefined ? {} : { locale: response.locale }),
    ...(response.zoneInfo === undefined ? {} : { zoneInfo: response.zoneInfo }),
    ...(response.updatedAt === undefined ? {} : { updatedAt: response.updatedAt }),
  };
}

function parseAuthenticationResponse(payload: unknown): {
  readonly session: ReturnType<typeof toAuthSession>;
  readonly user: AuthenticatedUser;
} {
  const parsed = authenticationResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError('server', 'The identity server returned an unrecognized response.', {
      cause: parsed.error,
    });
  }

  const response = parsed.data;
  if (!response.isSuccess) {
    throw new ApiError('validation', response.error ?? 'Sign in failed.');
  }

  if (
    response.accessToken === null ||
    response.accessToken === undefined ||
    response.expiresIn === undefined ||
    response.user === null ||
    response.user === undefined
  ) {
    throw new ApiError('server', 'The identity server returned an incomplete session.');
  }

  const user = mapUser(response.user);
  return {
    user,
    session: toAuthSession(
      {
        accessToken: response.accessToken,
        expiresIn: response.expiresIn,
        tokenType: response.tokenType,
      },
      user
    ),
  };
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthenticatedUser> {
    try {
      const response = await authApiClient.post<unknown>(API_ENDPOINTS.auth.login, {
        username: credentials.username,
        password: credentials.password,
        rememberMe: credentials.rememberMe,
        clientId: config.authClientId,
        scope: config.authScope,
      });

      const { session, user } = parseAuthenticationResponse(response.data);
      tokenService.save(session);
      return user;
    } catch (error) {
      tokenService.clear();
      throw toApiError(error);
    }
  }

  async refreshAccessToken(): Promise<string> {
    try {
      const response = await authApiClient.post<unknown>(API_ENDPOINTS.auth.refresh, {
        clientId: config.authClientId,
      });
      const { session } = parseAuthenticationResponse(response.data);
      tokenService.save(session);
      return session.accessToken;
    } catch (error) {
      tokenService.clear();
      throw toApiError(error);
    }
  }

  async restoreSession(): Promise<AuthenticatedUser | null> {
    const session = tokenService.load();
    if (session !== null && !tokenService.isExpired(session)) return session.user;

    try {
      await this.refreshAccessToken();
      return tokenService.load()?.user ?? null;
    } catch (error) {
      const apiError = toApiError(error);
      tokenService.clear();

      // A rejected refresh cookie means there is no session. Connectivity and server
      // failures are different: surface them so the login page can explain why restore
      // failed instead of pretending the user deliberately signed out.
      if (apiError.kind === 'unauthorized') return null;
      throw apiError;
    }
  }

  async logout(): Promise<void> {
    tokenService.clear();

    try {
      await authApiClient.post(API_ENDPOINTS.auth.logout, {
        clientId: config.authClientId,
      });
    } catch {
      // Local logout is authoritative; server logout is best effort.
    }
  }
}

export const authService = new AuthService();
